import { UtilityService } from "../../common/services/utility.service";
import { config } from "../app-config";
import { Building, Project, Site, SiteBorder } from "../../confi-state";
import { apartmentMapping, ColumnInfo } from "../../models/column-info";
import { alignment } from "../../components/BuildingConf/models/alignment.enum";
import { Column, UnitAmount } from "../../components/BuildingConf/models/column";
import { buildingType } from "../../components/BuildingConf/models/building-type.enum";
import { UnitsAmenities } from "../../components/BuildingConf/models/units-amenities";
import { getEnvKey } from "../../environments";
import { environment } from "../../environment.enum";
import { Part } from "../../components/BuildingConf/models/part";
import { ColumnDefinition, inclusionEnum } from "../../components/BuildingConf/models/ColumnDefinition/column-definition";
import { SectionJSON, UnitJ } from "../../models/section-json";

export type ColDefOption<ColType> = {
    id?: string,
    name: string,
    subname: string,
    unitDetails: { unit: string, details: string }[],
    type: ColType,
    squareMeters: number
}

export class BuildingHelperService {

    utilityService: UtilityService;

    constructor(utilityService: UtilityService) {
        this.utilityService = utilityService;
    }

    addStaircasesToBodyColumns<T extends { type?: string, bays?: number }>(allStairCaseColumns: T[], bodyColumns: T[]): T[] {
        let res;
        if (allStairCaseColumns.length > 0 && bodyColumns.length > 0) {
            const columnGroups = this.chunkify(bodyColumns, allStairCaseColumns.length + 1, true);

            columnGroups.forEach((s) => {
                if (allStairCaseColumns.length > 0) {
                    s.push(allStairCaseColumns.pop())
                }
            })
            res = columnGroups.flat();
        } else if (bodyColumns.length === 0) {
            res = allStairCaseColumns;
        } else {
            res = bodyColumns;
        }
        return res;
    }

    chunkify(a, n, balanced) {

        if (n < 2)
            return [a];

        var len = a.length,
            out = [],
            i = 0,
            size;

        if (len % n === 0) {
            size = Math.floor(len / n);
            while (i < len) {
                out.push(a.slice(i, i += size));
            }
        }

        else if (balanced) {
            while (i < len) {
                size = Math.ceil((len - i) / n--);
                out.push(a.slice(i, i += size));
            }
        }

        else {

            n--;
            size = Math.floor(len / n);
            if (len % size === 0)
                size--;
            while (i < size * n) {
                out.push(a.slice(i, i += size));
            }
            out.push(a.slice(size * n));

        }

        return out;
    }

    orderStaircasesInBody<T extends { type?: string, bays?: number }>(moosPlazaAlignment: alignment, allCoreColumns: T[], totalMiddleBays: any, shuffledColumns: T[], columns: T[], isMoosPlazaColumn: (type: string) => boolean): T[] {
        let result = [...shuffledColumns];
        if (moosPlazaAlignment === alignment.center) {
            if (!allCoreColumns || allCoreColumns.length === 0) {
                const halfBays = Math.floor(totalMiddleBays / 2);
                let indexAfterWhichToPlaceCentralMoosPlaza = 0;
                let currentBay = 0;
                shuffledColumns.forEach((col, index) => {
                    currentBay += col.bays;
                    if (currentBay > halfBays && indexAfterWhichToPlaceCentralMoosPlaza === 0) {
                        indexAfterWhichToPlaceCentralMoosPlaza = index;
                    }
                });
                const item = columns.find(column => isMoosPlazaColumn(column.type));
                if (item) {
                    shuffledColumns.splice(indexAfterWhichToPlaceCentralMoosPlaza, 0, item);
                }
                result = shuffledColumns;
            } else {
                // middle staircase is replaced by moosplein
                const item = columns.find(column => isMoosPlazaColumn(column.type));
                if (item) {
                    allCoreColumns.splice(Math.floor(allCoreColumns.length / 2), 0, item);
                }
                result = this.addStaircasesToBodyColumns(allCoreColumns, shuffledColumns);
            }
        } else if (allCoreColumns.length > 0) {
            result = this.addStaircasesToBodyColumns(allCoreColumns, shuffledColumns);
        }
        return result;
    }

    cloneBuilding(building: Building): Building {
        let result: Building;
        result = {
            ...building,
            props: { ...building.props, parts: this.cloneParts(building.props.parts) }
        }
        if (building.refined) {
            result.props.partsRefined = [...result.props.partsRefined.map(pr => ({ ...pr, parts: this.cloneParts(pr.parts) }))];
        }
        return result;
    }

    cloneParts(parts: (Part<any, any>)[]) {
        return parts.map(p => ({
            ...p,
            columns: p.columns.map(c => ({ ...c, id: Math.random().toString(), segments: [...c.segments.map(s => ({ ...s }))] }))
        }))
    }

    // not that buildings here are from all of the sites
    includeAdditionalData(project: Project, sites: Site[], buildings: Building[]): { project: Project, sites: Site[], buildings: Building[] } {
        sites.forEach(site => {
            const siteBuildings = buildings.filter(b => b.siteId === site.id);
            site.cost = this.calculateCostOfSite(siteBuildings);
            let area = this.siteArea(site.border);
            site.area = area;
            site.builtArea = this.calculateBuiltAreaOfSite(siteBuildings);
            site.builtAreaPercentage = this.calculateBuiltAreaPercentageOfSite(site, siteBuildings);
        })
        buildings.forEach(building => {
            const props = building.props
            if (props) {
                building.info = {
                    cost: this.calculateCost(props.info.totalGFA),
                    nrAppartments: this.calculateNrOfAppartments(props.totalAmountOfAllUnits)
                }
            } else {
                building.info = {
                    cost: 0,
                    nrAppartments: 0
                }
            }
        });
        return { project, sites, buildings };
    }

    calculateCost(totalGFA: number) {
        return Math.round(totalGFA * getEnvKey((process.env.REACT_APP_ENVIRONMENT as environment), 'pricePerSquareMeterGFA'));
    }

    calculateNrOfAppartments(totalAmountOfAllUnits: UnitAmount[]) {
        return totalAmountOfAllUnits.map(u => u.amount).reduce((p, c) => p + c, 0)
    }

    calculateCostOfSite(buildings: Building[]) {
        return buildings.map(building => {
            const props = building.props;
            if (props && props.info && props.info.totalGFA) {
                return props.info.totalGFA * getEnvKey((process.env.REACT_APP_ENVIRONMENT as environment), 'pricePerSquareMeterGFA');
            } else {
                return 0;
            }
        }).reduce((p, c) => p + c, 0)
    }

    calculateTotalAppartmentsOfSite(buildings: Building[]): number {
        let result = 0;
        if (buildings && buildings.length > 0) {
            result = buildings.map(building => {
                if (building.props && building.props.totalAmountOfAllUnits) {
                    return building.props.totalAmountOfAllUnits.map(u => u.amount).reduce((p, c) => p + c, 0);
                } else {
                    return 0;
                }
            }).reduce((p, c) => p + c, 0);
        }
        return result;
    }

    getNameBuildingType(type: buildingType, allCaps = false) {
        let result = '';
        switch (type) {
            case buildingType.hybrid:
                result = 'Hybride';
                break;
            case buildingType.doubleLoadedCorridor:
                result = 'Corridor';
                break;
            case buildingType.singleLoadedCorridor:
                result = 'Galerij';
                break;
            case buildingType.rowhouse:
                result = 'Rijwoning';
                break;
            case buildingType.tower:
                result = 'Toren';
                break;
            default:
                break;
        }
        if (allCaps) {
            result = result.toUpperCase();
        }
        return result;
    }

    calculateNrOfUnitsPerType(buildings: Building[]) {
        const result = [{
            label: 'Galerij',
            total: 0,
            types: [
                {
                    type: 'XS',
                    nr: 0
                },
                {
                    type: 'S',
                    nr: 0
                },
                {
                    type: 'M',
                    nr: 0
                },
                {
                    type: 'L',
                    nr: 0
                },
                {
                    type: 'XL',
                    nr: 0
                }
            ]
        }, {
            label: 'Huizen',
            total: 0,
            types: [
                {
                    type: 'S',
                    nr: 0
                },
                {
                    type: 'M',
                    nr: 0
                },
                {
                    type: 'L',
                    nr: 0
                },
            ]
        },
        {
            label: 'Corridor',
            total: 0,
            types: [
                {
                    type: 'XS',
                    nr: 0
                },
                {
                    type: 'S',
                    nr: 0
                },
                {
                    type: 'M',
                    nr: 0
                },
                {
                    type: 'L',
                    nr: 0
                },
                {
                    type: 'XL',
                    nr: 0
                }
            ]
        }];
        buildings.forEach(b => {
            if (b.props && b.props.totalAmountOfAllUnits) {
                b.props.totalAmountOfAllUnits.forEach(total => {
                    if (total.type.includes('C-')) {
                        const re = result.find(r => r.label === 'Corridor');
                        this.updateCalc(total, re);
                    } else if (total.type.includes('G-')) {
                        const re = result.find(r => r.label === 'Galerij');
                        this.updateCalc(total, re);
                    } else if (total.type.includes('H-')) {
                        const re = result.find(r => r.label === 'Huizen');
                        this.updateCalc(total, re);
                    }
                });
            }
        })
        return result;
    }

    private updateCalc(total: UnitAmount, re: { label: string; total: number; types: { type: string; nr: number; }[]; }) {
        const t = total.type.split('-')[1];
        const ty = re.types.find(r => r.type === t);
        ty.nr += total.amount;
        re.total += total.amount;
    }

    calculateTotalGFAOfSite(buildings: Building[]): number {
        let result = 0;
        if (buildings && buildings.length > 0) {
            result = buildings.map(building => {
                if (building.props && building.props.info) {
                    return building.props.info.totalGFA;
                } else {
                    return 0;
                }
            }).reduce((p, c) => p + c, 0);
        }
        return result;
    }

    calculateTotalUFAOfSite(buildings: Building[]): number {
        let result = 0;
        if (buildings && buildings.length > 0) {
            result = buildings.map(building => {
                if (building.props && building.props.info) {
                    return building.props.info.totalUFA;
                } else {
                    return 0;
                }
            }).reduce((p, c) => p + c, 0);
        }
        return result;
    }

    calculateBuiltAreaOfSite(buildings: Building[]): number {
        return buildings.map(building => {

            if (building.props) {
                return +building.props.buildingWidth * +building.props.buildingLength;
            } else {
                return 0;
            }
        }).reduce((p, c) => p + c, 0);
    }

    calculateBuiltAreaPercentageOfSite(site: Site, buildings: Building[]): number {
        let area = 0;
        const totalSiteArea = this.siteArea(site.border);;
        if (totalSiteArea > 0 && buildings && buildings.length > 0) {
            const builtArea = this.calculateBuiltAreaOfSite(buildings);
            area = (builtArea / totalSiteArea) * 100;
        }
        return area;
    }

    getApartmentForUnitType(uType: string) {
        const apartmentMap = apartmentMapping();
        const apartment = apartmentMap.find(ap => ap.map.includes(uType as any));
        return apartment?.apartment;
    }

    getBuildingFloors(building: Building) {
        const floorHeightOfBuilding = this.floorHeightOfBuilding(building.type);
        let floorNr;
        if ((building.props as any).plinthType === 'Custom') {
            floorNr = Math.floor((building.props.buildingHeight - (building.props as any).customPlinthHeight) / floorHeightOfBuilding) + 1;
        } else {
            floorNr = Math.floor(building.props.buildingHeight / floorHeightOfBuilding);
        }

        const floors = []
        for (let i = 0; i < floorNr; i++) {
            floors.push(i);
        }
        return floors;
    }

    getColumnSegment(column: Column<string, string, string, string>, segmentType: string) {
        return column?.segments.find(s => s.type === segmentType);
    }

    computePartInfo(parts: { columns?: { unitsAndAmenities?: UnitsAmenities[] }[] }[]): ColumnInfo {
        const columns = parts.map(s => s.columns).flat();
        return this.computeColumnInfo(columns);
    }

    computeColumnInfo(columns: { unitsAndAmenities?: UnitsAmenities[] }[]): ColumnInfo {
        const apartmentMap = apartmentMapping();
        const totalUGFA = columns.map(c => {
            return c.unitsAndAmenities.map(ua => {
                const uaTot = [];
                if (ua.units) {
                    ua.units.forEach(u => {
                        const apartment = apartmentMap.find(ap => ap.map.includes(u.unitType as any));
                        if (apartment) {
                            apartment.amount++;
                        } else {
                            console.error(`app did not find apartment in the map: ${apartmentMap}, for unit type ${u.unitType}`);
                        }
                    })
                    uaTot.push(...ua.units);
                }
                if (ua.amenities) {
                    uaTot.push(...ua.amenities);
                }
                return uaTot;
            })
        }).flat().flat();
        apartmentMap.forEach(a => delete a.map);
        const info = {
            totalGFA: totalUGFA.map(s => s.GFA ? s.GFA : 0).reduce((p, c) => p + c, 0),
            totalUFA: totalUGFA.map(s => s.UFA ? s.UFA : 0).reduce((p, c) => p + c, 0),
            apartmentData: apartmentMap
        };
        return info;
    }

    getMiddleFloors(plinthTypeIn: 'Custom' | string, buildingHeight: number, customPlinthHeight: number, crownFloors: number, plinthFloors: number, bt: buildingType) {
        let floors;

        const floorHeightOfBuilding = this.floorHeightOfBuilding(bt);

        if (plinthTypeIn === 'Custom') {
            floors = Math.floor((buildingHeight - customPlinthHeight) / floorHeightOfBuilding) - crownFloors;
        } else {
            floors = Math.floor(buildingHeight / floorHeightOfBuilding) - plinthFloors - crownFloors;
        }
        let result = floors > 0 ? floors : 0;
        return result;
    }

    siteArea(border: SiteBorder) {
        let area = 0;
        if (border) {
            if (border.parcels && border.parcels.length > 0) {
                border.parcels.forEach(a => {
                    area += a.size;
                });
            } else if (border.polygons) {
                // sum up polygon areas
                border.polygons.forEach(p => area += this.utilityService.calculatePolygonArea(p.path));
                // remove hole areas
                border.polygons.forEach(p => p.holes.forEach(h => area -= this.utilityService.calculatePolygonArea(h)));
            }
        }
        return area;
    }

    floorHeightOfBuilding(bt: buildingType): number {
        let floorHeightOfBuilding = 0;
        if (bt === buildingType.hybrid || bt === buildingType.singleLoadedCorridor) {
            floorHeightOfBuilding = config.hybridFloorHeight;
        } else if (bt === buildingType.doubleLoadedCorridor) {
            floorHeightOfBuilding = config.corridordFloorHeight;
        } else if (bt === buildingType.tower) {
            floorHeightOfBuilding = config.towerFloorHeight;
        } else if (bt === buildingType.rowhouse) {
            floorHeightOfBuilding = config.rowhouseFloorHeight;
        }
        return floorHeightOfBuilding;
    }

    getColumnOptions<ColType, SubType, SegType, Variant, ColSub>(
        colDef: ColumnDefinition<ColType, SubType, SegType, Variant, ColSub>,
        baseSegment: SegType,
        sectionJson: SectionJSON,
    ): ColDefOption<ColType> {

        const isMoosPlaza = colDef.columnSubtypes.includes('moosplaza' as any);

        const middleDef = colDef.segmentDefinitions.find(sd => sd.type === baseSegment);
        const sectionType = middleDef.floorVariants[0].variants[0].sections[0];
        const sectionDescription = sectionJson.sections.find(s => s.sectionType === sectionType);

        const allUnitDefinitionsInSection: UnitJ[] = [];
        if (sectionDescription && sectionDescription.units.length > 0) {
            sectionDescription.units.forEach(unit => {
                const unitDef = sectionJson.units.find(u => (u.unitType === unit.unitType));
                allUnitDefinitionsInSection.push({ ...unitDef });
            });
        }

        const uniqueDefs: { unitDef: UnitJ; amount: number; }[] = [];
        allUnitDefinitionsInSection.forEach(ud => {
            if (!uniqueDefs.find(u => u.unitDef.baseType === ud.baseType)) {
                uniqueDefs.push({ unitDef: ud, amount: 1 });
            } else {
                const toUpdate = uniqueDefs.find(d => d.unitDef.baseType === ud.baseType);
                toUpdate.amount += 1;
            }
        });

        let name = uniqueDefs.map(ud => {
            if (ud.amount === 1) {
                return ud.unitDef.uiName;
            } else {
                return `${ud.amount} x ${ud.unitDef.uiName}`;
            }
        }).join(', ');

        if (isMoosPlaza) {
            name += ' (Entree)';
        }

        let subname = '';
        if (colDef.inclusions.find(s => s === inclusionEnum.elevator)) {
            subname = 'lift'
        } else if (colDef.inclusions.find(s => s === inclusionEnum.staircase)) {
            subname = 'trappenhuis'
        }

        if (!name && subname) {
            name = subname;
            subname = undefined;
        } else if (!name) {
            name = colDef.uiName;
        }

        let unitDetails = uniqueDefs.map(ud => {
            return { unit: ud.unitDef.uiName, details: `${ud.unitDef.GFA} ㎡ en ${ud.unitDef.bedrooms ? ud.unitDef.bedrooms : 'geen'} slaapkamer(s)` };
        });

        const squareMeters = uniqueDefs.map(ud => {
            if (ud.amount === 1) {
                return ud.unitDef.GFA;
            } else {
                return ud.unitDef.GFA * ud.amount;
            }
        });
        return { name, subname, unitDetails, squareMeters: squareMeters.reduce((p, c) => p + c, 0), type: colDef.type as ColType };
    }

    getHighestNameNr(names: string[]) {
        const numbers = names.map(n => n.split(' ').pop());
        let highest = 0;
        numbers.forEach(n => {
            let c = +n;
            if (!isNaN(c)) {
                if (c > highest) {
                    highest = c;
                }
            }
        });
        return highest;
    }

}